import React, { useState, useEffect, useCallback } from "react";

// Phase 6A.3 — Local SEO Health panel for the client detail view.
// Loads /api/clients/:id/seo-health and renders three operator-facing
// alerts:
//   1. Ratio view — current local/authority mix vs target. Red when
//      unconfigured (blocks scheduler entirely), amber when off-target
//      by >5pp, green within ±5pp.
//   2. Inventory alerts — per-location count of UNUSED local keywords.
//      Below 5 → amber, with a CTA to add more keywords.
//   3. Missing SAPs — locations with ≥1 local keyword but no
//      service_area_pages entry. Blocks scheduling for those keywords.
//
// Props:
//   client    — selectedClient object
//   api       — backend base URL
//   authFetch — auth-injecting fetch wrapper from App.js
export default function ClientSeoHealthPanel({ client, api, authFetch }) {
  const [data, setData] = useState(null);
  const [publishHealth, setPublishHealth] = useState(null); // Phase 7.1
  const [schedulerHealth, setSchedulerHealth] = useState(null); // Phase 7.3
  const [costEfficiency, setCostEfficiency] = useState(null); // Phase 7.5
  const [postPerformance, setPostPerformance] = useState(null); // Phase 7.2
  const [perfExpanded, setPerfExpanded] = useState(false); // 7.2 card defaults to collapsed (Codex: panel is crowded)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!client?.id) return;
    setLoading(true);
    setError("");
    try {
      // Phase 7.1 + 7.2 + 7.3 + 7.5 — fetch all five health endpoints
      // in parallel. Only seo-health is required; the rest are
      // non-fatal (cards just don't render).
      const [seoRes, phRes, shRes, ceRes, ppRes] = await Promise.all([
        authFetch(`${api}/api/clients/${client.id}/seo-health`),
        authFetch(`${api}/api/clients/${client.id}/publish-health`),
        authFetch(`${api}/api/clients/${client.id}/scheduler-health`),
        authFetch(`${api}/api/clients/${client.id}/cost-efficiency`),
        authFetch(`${api}/api/clients/${client.id}/post-performance`),
      ]);
      if (!seoRes.ok) throw new Error((await seoRes.text()) || `HTTP ${seoRes.status}`);
      const d = await seoRes.json();
      setData(d);
      if (phRes.ok) {
        try { setPublishHealth(await phRes.json()); }
        catch { setPublishHealth(null); }
      } else {
        setPublishHealth(null);
      }
      if (shRes.ok) {
        try { setSchedulerHealth(await shRes.json()); }
        catch { setSchedulerHealth(null); }
      } else {
        setSchedulerHealth(null);
      }
      if (ceRes.ok) {
        try { setCostEfficiency(await ceRes.json()); }
        catch { setCostEfficiency(null); }
      } else {
        setCostEfficiency(null);
      }
      if (ppRes.ok) {
        try { setPostPerformance(await ppRes.json()); }
        catch { setPostPerformance(null); }
      } else {
        setPostPerformance(null);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client?.id, api, authFetch]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={styles.shell}>
        <div style={styles.loading}>Loading Local SEO Health…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.shell}>
        <div style={styles.errorBanner}>Failed to load SEO health: {error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { ratio, inventory, missing_saps, approval, service_locations } = data;

  // Phase 7.4 — Content Mix severity uses Last 30 + Next Scheduled
  // slices, not all-time. Per Codex:
  //   GREEN  both slices within ±10pp of target
  //   AMBER  either slice > 20pp off target
  //   RED    both slices > 30pp off target
  // Falls back to legacy all-time deviation if the new slices aren't
  // present (backend caching / pre-7.4 deploy window).
  const last30 = ratio.last_30_days || null;
  const nextSched = ratio.next_scheduled || null;
  const absDev = (v) => v == null ? null : Math.abs(v);
  const ratioSeverity = (() => {
    if (!ratio.configured) return "red";
    const l30dev = last30 ? absDev(last30.deviation_pp) : null;
    const nsdev  = nextSched ? absDev(nextSched.deviation_pp) : null;
    if (l30dev == null && nsdev == null) {
      return Math.abs(ratio.deviation_pp || 0) > 5 ? "amber" : "green";
    }
    const bothPresent = l30dev != null && nsdev != null;
    if (bothPresent && l30dev > 30 && nsdev > 30) return "red";
    if ((l30dev != null && l30dev > 20) || (nsdev != null && nsdev > 20)) return "amber";
    if ((l30dev == null || l30dev <= 10) && (nsdev == null || nsdev <= 10)) return "green";
    return "amber";
  })();
  const inventorySeverity =
    inventory.some(i => i.alert) ? "amber" : "green";
  const sapSeverity =
    missing_saps.length > 0 ? "amber" : "green";
  // Phase 6A.5: service-area severity.
  // RED when not configured (scheduler blocks all local keywords).
  // GREEN when configured. The previous AMBER state for "out-of-area
  // library keywords" was removed in 6A.7 — it counted shared library
  // entries the scheduler already filters out, so it was raising false
  // alarms on healthy clients.
  const serviceLocationsSeverity =
    !service_locations || !service_locations.configured ? "red"
    : "green";
  // Phase 6A.2.a — approval-queue severity.
  // RED when zero approved + ≥1 pending (scheduler blocked).
  // AMBER when zero approved + zero pending (operator hasn't been
  //                given anything to approve yet — they need to add
  //                or refresh keywords first).
  // GREEN when ≥1 approved (scheduler can pick).
  const approvalSeverity =
    !approval ? "green"
    : approval.approved_count > 0 ? "green"
    : approval.pending_count > 0 ? "red"
    : "amber";

  // Phase 7.1 — include publish-health severity in the top-level alert
  // signal so the panel auto-expands when publish-side metrics go red.
  const publishHealthSeverity = (() => {
    if (!publishHealth) return "green";
    const a = publishHealth.pass_rate?.severity;
    const b = publishHealth.metadata_coverage?.severity;
    if (a === "red" || b === "red") return "red";
    if (a === "amber" || b === "amber") return "amber";
    return "green";
  })();
  // Phase 7.3 — same for scheduler-health
  const schedulerHealthSeverity = (() => {
    if (!schedulerHealth) return "green";
    const s = schedulerHealth.stuck?.severity;
    const l = schedulerHealth.pickup_latency?.severity;
    const g = schedulerHealth.gate_blocks?.severity;
    if (s === "red" || l === "red" || g === "red") return "red";
    if (s === "amber" || l === "amber" || g === "amber") return "amber";
    return "green";
  })();
  const anyAlerts = ratioSeverity !== "green" || inventorySeverity !== "green" || sapSeverity !== "green" || approvalSeverity !== "green" || serviceLocationsSeverity !== "green" || publishHealthSeverity !== "green" || schedulerHealthSeverity !== "green";

  // Collapsed (all-green) state — single line summary
  if (!anyAlerts && !expanded) {
    return (
      <div style={{ ...styles.shell, borderLeft: "3px solid #22c55e" }}>
        <div style={styles.collapsedRow}>
          <span style={styles.collapsedIcon}>✓</span>
          <div style={styles.collapsedText}>
            <strong style={{ color: "#fff" }}>Local SEO Health:</strong>{" "}
            <span style={{ color: "#86efac" }}>All clear</span>{" "}
            · {ratio.current_local_pct}% local / {100 - ratio.current_local_pct}% authority (target {ratio.target_local_pct}%)
            · {inventory.filter(i => !i.alert).length} location{inventory.length === 1 ? "" : "s"} stocked
            · {data.configured_saps.length} SAP{data.configured_saps.length === 1 ? "" : "s"} configured
          </div>
          <button onClick={() => setExpanded(true)} style={styles.collapsedToggle}>Show details</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.shell, borderLeft: `3px solid ${anyAlerts ? "#d60000" : "#22c55e"}` }}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.eyebrow}>Client Setup</div>
          <div style={styles.title}>Local SEO Health</div>
        </div>
        <button onClick={load} style={styles.refreshBtn} title="Refresh">↻</button>
      </div>

      <div style={styles.grid}>
        {/* ── Phase 6A.2.a Approval Queue ── */}
        {approval && (
          <SubCard severity={approvalSeverity} label="Awaiting Your Review">
            {approval.pending_count === 0 && approval.approved_count === 0 ? (
              <div>
                <div style={styles.statBig}>Empty</div>
                <div style={styles.statSub}>No keywords queued for this client this month. Add from the Keyword Library or refresh research/gap lists.</div>
              </div>
            ) : approval.pending_count > 0 ? (
              <div>
                <div style={{ ...styles.statBig, color: "#fca5a5" }}>{approval.pending_count} pending</div>
                <div style={styles.statSub}>
                  Scheduler will <strong style={{ color: "#fff" }}>not</strong> pick any keywords until you Approve them. {approval.approved_count > 0 ? `${approval.approved_count} already approved.` : ""}
                </div>
                <div style={styles.statTiny}>Open Scheduled Queue tab → defaults to Pending filter → Approve / Skip / Edit per row.</div>
              </div>
            ) : (
              <div>
                <div style={{ ...styles.statBig, color: "#86efac" }}>{approval.approved_count} approved</div>
                <div style={styles.statSub}>Scheduler will pick from these. No keywords currently pending review.</div>
              </div>
            )}
          </SubCard>
        )}

        {/* ── Phase 6A.5 Service Locations ── */}
        {service_locations && (
          <SubCard severity={serviceLocationsSeverity} label="Service Locations">
            {!service_locations.configured ? (
              <div>
                <div style={{ ...styles.statBig, color: "#fca5a5" }}>Not configured</div>
                <div style={styles.statSub}>
                  Scheduler will <strong style={{ color: "#fff" }}>not</strong> pick any LOCAL keywords until you declare which locations this client services.
                </div>
                <div style={styles.statTiny}>Edit client settings → Local / Authority Content Mix → "Service Locations" list.</div>
              </div>
            ) : (
              <div>
                <div style={styles.statBig}>{service_locations.locations.length} location{service_locations.locations.length === 1 ? "" : "s"}</div>
                <div style={styles.statSub}>
                  {service_locations.locations.slice(0, 4).join(", ")}
                  {service_locations.locations.length > 4 ? ` + ${service_locations.locations.length - 4} more` : ""}
                </div>
                {/* Phase 6A.7 — removed the "library local keyword targets
                    location outside this client's service area" warning.
                    It was counting keywords from the SHARED industry library
                    (e.g. columbia_sc tagged on the HVAC library) that the
                    scheduler already correctly filters out for this client.
                    The operator read "scheduler" and thought their queue had
                    out-of-area entries — which it never does. The scheduler
                    behavior was already correct; only the message was wrong. */}
              </div>
            )}
          </SubCard>
        )}

        {/* ── Ratio view ── Phase 7.4: Last 30 + Next Scheduled headline,
             all-time as small context line. */}
        <SubCard severity={ratioSeverity} label="Content Mix">
          {!ratio.configured ? (
            <div>
              <div style={styles.statBig}>Not configured</div>
              <div style={styles.statSub}>Set the local/authority ratio in client settings — scheduler is blocked until then.</div>
            </div>
          ) : (() => {
            // Reusable slice renderer
            const renderSlice = (slice, label, emptyMsg) => {
              if (!slice || slice.total_posts === 0) {
                return (
                  <div style={{ flex: 1, padding: 10, background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 6 }}>
                    <div style={{ fontSize: 9, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.14em", textTransform: "uppercase", color: "#666", marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: 11, color: "#555", fontStyle: "italic" }}>{emptyMsg}</div>
                  </div>
                );
              }
              const devColor = slice.deviation_pp == null ? "#aaa"
                : Math.abs(slice.deviation_pp) <= 10 ? "#86efac"
                : Math.abs(slice.deviation_pp) <= 20 ? "#eab308"
                : "#fca5a5";
              return (
                <div style={{ flex: 1, padding: 10, background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 6 }}>
                  <div style={{ fontSize: 9, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.14em", textTransform: "uppercase", color: "#fca5a5", marginBottom: 6 }}>{label}</div>
                  <div style={styles.ratioRow}>
                    <span style={{ ...styles.ratioPill, background: "rgba(214,0,0,0.15)", color: "#fca5a5" }}>{slice.current_local_pct}% local</span>
                    <span style={styles.ratioDivider}>/</span>
                    <span style={{ ...styles.ratioPill, background: "rgba(255,255,255,0.06)", color: "#aaa" }}>{100 - slice.current_local_pct}% authority</span>
                  </div>
                  <div style={styles.barWrap}>
                    <div style={{ ...styles.barFill, width: `${slice.current_local_pct}%`, background: "#d60000" }} />
                  </div>
                  <div style={{ ...styles.statTiny, marginTop: 4 }}>
                    <span style={{ color: devColor }}>
                      {slice.deviation_pp > 0 ? `+${slice.deviation_pp}` : slice.deviation_pp}pp vs target
                    </span>
                    <span style={{ color: "#444" }}> · </span>
                    <span style={{ color: "#888" }}>{slice.local_posts}L / {slice.authority_posts}A · {slice.total_posts} total</span>
                  </div>
                </div>
              );
            };
            return (
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>
                  Target: <strong style={{ color: "#fff" }}>{ratio.target_local_pct}% local</strong>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  {renderSlice(last30, "Last 30 days published", "No posts published in last 30 days")}
                  {renderSlice(nextSched, "Next scheduled", "Queue empty — refresh research / gap")}
                </div>
                {ratio.all_time && ratio.all_time.total_posts > 0 && (
                  <div style={{ ...styles.statTiny, color: "#555", borderTop: "1px solid #1a1a1a", paddingTop: 6, marginTop: 6 }}>
                    All-time context: {ratio.all_time.current_local_pct}% local across {ratio.all_time.total_posts} posts (
                    {ratio.all_time.local_posts}L / {ratio.all_time.authority_posts}A)
                  </div>
                )}
              </div>
            );
          })()}
        </SubCard>

        {/* ── Inventory alerts ── */}
        <SubCard severity={inventorySeverity} label="Local Keyword Inventory">
          {inventory.length === 0 ? (
            <div style={styles.statSub}>No local keywords for this industry yet.</div>
          ) : (
            <div>
              <div style={styles.statTiny}>Unused local keywords per location (warning &lt; {inventory[0]?.threshold || 5}):</div>
              <div style={styles.invList}>
                {inventory.slice(0, 6).map((row) => (
                  <div key={row.location} style={{ ...styles.invRow, color: row.alert ? "#fca5a5" : "#aaa" }}>
                    <span style={styles.invLoc}>{row.location}</span>
                    <span style={styles.invCount}>{row.unused_local}</span>
                    {!row.has_sap && <span style={styles.invMissingSap} title="No SAP configured for this location">(no SAP)</span>}
                  </div>
                ))}
                {inventory.length > 6 && (
                  <div style={styles.statTiny}>+ {inventory.length - 6} more</div>
                )}
              </div>
            </div>
          )}
        </SubCard>

        {/* ── Missing SAPs ── */}
        <SubCard severity={sapSeverity} label="Missing Service Area Pages">
          {missing_saps.length === 0 ? (
            <div style={styles.statSub}>All locations covered.</div>
          ) : (
            <div>
              <div style={styles.statTiny}>Locations with keywords but no SAP configured (scheduler blocks these):</div>
              <div style={styles.invList}>
                {missing_saps.slice(0, 6).map((row) => (
                  <div key={row.location} style={{ ...styles.invRow, color: "#fca5a5" }}>
                    <span style={styles.invLoc}>{row.location}</span>
                    <span style={styles.invCount}>{row.keyword_count} kw</span>
                  </div>
                ))}
                {missing_saps.length > 6 && (
                  <div style={styles.statTiny}>+ {missing_saps.length - 6} more</div>
                )}
              </div>
              <div style={styles.statTiny}>Add SAPs in client settings → Local / Authority Content Mix.</div>
            </div>
          )}
        </SubCard>

        {/* ── Phase 7.1 — Publish Health ──
            Throughput, pass rate, hold breakdown, metadata coverage, QA, retries.
            Non-fatal: only renders when publishHealth fetched OK. */}
        {publishHealth && (
          <SubCard
            severity={
              // Card-level severity = worst of pass_rate + metadata_coverage
              (() => {
                const a = publishHealth.pass_rate?.severity;
                const b = publishHealth.metadata_coverage?.severity;
                if (a === "red" || b === "red") return "red";
                if (a === "amber" || b === "amber") return "amber";
                if (a === "neutral" && b === "neutral") return "amber"; // no data
                return "green";
              })()
            }
            label="Publish Health (last 30 days)"
          >
            <div>
              {/* THROUGHPUT */}
              <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ ...styles.statBig, fontSize: 22 }}>{publishHealth.throughput.last_30_days}</div>
                  <div style={{ ...styles.statTiny, marginTop: -2 }}>posts published / 30d</div>
                </div>
                <div>
                  <div style={{ ...styles.statBig, fontSize: 22, color: "#aaa" }}>{publishHealth.throughput.last_7_days}</div>
                  <div style={{ ...styles.statTiny, marginTop: -2 }}>last 7d</div>
                </div>
              </div>

              {/* PASS RATE */}
              <div style={{ marginBottom: 8 }}>
                <div style={styles.ratioRow}>
                  <span style={{
                    ...styles.ratioPill,
                    background: publishHealth.pass_rate.severity === "green" ? "rgba(34,197,94,0.15)"
                              : publishHealth.pass_rate.severity === "amber" ? "rgba(234,179,8,0.15)"
                              : publishHealth.pass_rate.severity === "red"   ? "rgba(214,0,0,0.15)"
                              : "rgba(255,255,255,0.06)",
                    color: publishHealth.pass_rate.severity === "green" ? "#86efac"
                         : publishHealth.pass_rate.severity === "amber" ? "#eab308"
                         : publishHealth.pass_rate.severity === "red"   ? "#fca5a5"
                         : "#aaa",
                  }}>
                    {publishHealth.pass_rate.pass_pct == null ? "—" : `${publishHealth.pass_rate.pass_pct}% pass`}
                  </span>
                  <span style={styles.statTiny}>
                    {publishHealth.pass_rate.published_count} published / {publishHealth.pass_rate.active_held_count} held
                  </span>
                </div>
              </div>

              {/* METADATA COVERAGE */}
              <div style={{ marginBottom: 8 }}>
                <div style={styles.ratioRow}>
                  <span style={{
                    ...styles.ratioPill,
                    background: publishHealth.metadata_coverage.severity === "green" ? "rgba(34,197,94,0.15)"
                              : publishHealth.metadata_coverage.severity === "amber" ? "rgba(234,179,8,0.15)"
                              : publishHealth.metadata_coverage.severity === "red"   ? "rgba(214,0,0,0.15)"
                              : "rgba(255,255,255,0.06)",
                    color: publishHealth.metadata_coverage.severity === "green" ? "#86efac"
                         : publishHealth.metadata_coverage.severity === "amber" ? "#eab308"
                         : publishHealth.metadata_coverage.severity === "red"   ? "#fca5a5"
                         : "#aaa",
                  }}>
                    {publishHealth.metadata_coverage.complete_pct == null ? "—" : `${publishHealth.metadata_coverage.complete_pct}% metadata`}
                  </span>
                  <span style={styles.statTiny}>
                    title+meta+slug complete
                  </span>
                </div>
              </div>

              {/* QA SUMMARY */}
              {publishHealth.qa && publishHealth.qa.sample_size > 0 && (
                <div style={{ ...styles.statTiny, marginBottom: 8 }}>
                  QA: avg score <strong style={{ color: "#fff" }}>{publishHealth.qa.avg_score ?? "—"}</strong>
                  {publishHealth.qa.qa_pass_pct != null && (
                    <> · <strong style={{ color: "#fff" }}>{publishHealth.qa.qa_pass_pct}%</strong> passed</>
                  )}
                  {publishHealth.qa.avg_cycles != null && (
                    <> · <strong style={{ color: "#fff" }}>{publishHealth.qa.avg_cycles}</strong> cycles avg</>
                  )}
                </div>
              )}

              {/* HOLD BREAKDOWN */}
              {publishHealth.hold_breakdown.length > 0 ? (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #1a1a1a" }}>
                  <div style={{ ...styles.statTiny, color: "#fca5a5", marginBottom: 4 }}>
                    Active holds ({publishHealth.pass_rate.active_held_count}):
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {publishHealth.hold_breakdown.slice(0, 6).map((b) => (
                      <span key={b.held_reason} style={{
                        fontSize: 9, padding: "2px 6px", borderRadius: 3,
                        background: "rgba(214,0,0,0.08)", border: "1px solid rgba(214,0,0,0.3)",
                        color: "#fca5a5", fontFamily: "'Barlow Condensed', sans-serif",
                        letterSpacing: "0.08em", textTransform: "uppercase",
                      }} title={`${b.count} held with reason: ${b.held_reason}`}>
                        {b.held_reason} <strong>{b.count}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              ) : publishHealth.pass_rate.active_held_count === 0 ? (
                <div style={{ ...styles.statTiny, color: "#86efac", marginTop: 4 }}>No active holds.</div>
              ) : null}

              {/* RETRY RATE */}
              {publishHealth.retries.total_posts_with_metrics > 0 && (
                <div style={{ ...styles.statTiny, color: "#555", borderTop: "1px solid #1a1a1a", paddingTop: 6, marginTop: 8 }}>
                  Pipeline: avg {publishHealth.retries.avg_attempts_per_post} attempts/post
                  {publishHealth.retries.posts_needing_retry > 0 && (
                    <> · {publishHealth.retries.posts_needing_retry} needed retry</>
                  )}
                </div>
              )}
            </div>
          </SubCard>
        )}

        {/* ── Phase 7.3 — Scheduler Health ──
            Measures whether the scheduler is firing the pipeline on time.
            Distinct from Publish Health which measures pipeline outcomes. */}
        {schedulerHealth && (
          <SubCard severity={schedulerHealthSeverity} label="Scheduler Health (last 30 days)">
            <div>
              {/* TOP ROW — stuck/overdue + jobs */}
              <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{
                    ...styles.statBig, fontSize: 22,
                    color: schedulerHealth.stuck.severity === "red" ? "#fca5a5"
                         : schedulerHealth.stuck.severity === "amber" ? "#eab308"
                         : "#86efac",
                  }}>
                    {schedulerHealth.stuck.overdue_count}
                  </div>
                  <div style={{ ...styles.statTiny, marginTop: -2 }}>
                    stuck/overdue
                    {schedulerHealth.stuck.overdue_count > 0 && schedulerHealth.stuck.oldest_overdue_minutes > 0 && (
                      <> · oldest {formatMinutes(schedulerHealth.stuck.oldest_overdue_minutes)}</>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ ...styles.statBig, fontSize: 22 }}>{schedulerHealth.jobs.published_count}</div>
                  <div style={{ ...styles.statTiny, marginTop: -2 }}>jobs published</div>
                </div>
                {schedulerHealth.jobs.failed_count > 0 && (
                  <div>
                    <div style={{ ...styles.statBig, fontSize: 22, color: "#fca5a5" }}>{schedulerHealth.jobs.failed_count}</div>
                    <div style={{ ...styles.statTiny, marginTop: -2 }}>failed</div>
                  </div>
                )}
              </div>

              {/* PICKUP LATENCY */}
              {schedulerHealth.pickup_latency.sample_size > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={styles.ratioRow}>
                    <span style={{
                      ...styles.ratioPill,
                      background: schedulerHealth.pickup_latency.severity === "green" ? "rgba(34,197,94,0.15)"
                                : schedulerHealth.pickup_latency.severity === "amber" ? "rgba(234,179,8,0.15)"
                                : schedulerHealth.pickup_latency.severity === "red"   ? "rgba(214,0,0,0.15)"
                                : "rgba(255,255,255,0.06)",
                      color: schedulerHealth.pickup_latency.severity === "green" ? "#86efac"
                           : schedulerHealth.pickup_latency.severity === "amber" ? "#eab308"
                           : schedulerHealth.pickup_latency.severity === "red"   ? "#fca5a5"
                           : "#aaa",
                    }}>
                      avg {formatMinutes(schedulerHealth.pickup_latency.avg_minutes)} latency
                    </span>
                    <span style={styles.statTiny}>
                      p90 {formatMinutes(schedulerHealth.pickup_latency.p90_minutes)} · max {formatMinutes(schedulerHealth.pickup_latency.max_minutes)} ({schedulerHealth.pickup_latency.sample_size} jobs)
                    </span>
                  </div>
                </div>
              )}

              {/* GATE BLOCKS */}
              {schedulerHealth.gate_blocks.by_reason.length > 0 ? (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #1a1a1a" }}>
                  <div style={{ ...styles.statTiny, color: "#fca5a5", marginBottom: 4 }}>
                    Scheduler gate blocks ({schedulerHealth.gate_blocks.total_30d}):
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {schedulerHealth.gate_blocks.by_reason.slice(0, 6).map((g) => (
                      <span key={g.reason} style={{
                        fontSize: 9, padding: "2px 6px", borderRadius: 3,
                        background: "rgba(214,0,0,0.08)", border: "1px solid rgba(214,0,0,0.3)",
                        color: "#fca5a5", fontFamily: "'Barlow Condensed', sans-serif",
                        letterSpacing: "0.08em", textTransform: "uppercase",
                      }} title={`${g.count} blocks for: ${g.reason}`}>
                        {g.reason} <strong>{g.count}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ ...styles.statTiny, color: "#86efac", marginTop: 4 }}>No scheduler-side blocks.</div>
              )}

              {/* CANNIBALIZATION ADVISORY — kept separate per Codex 7.3 */}
              {schedulerHealth.advisories.cannibalization_count_30d > 0 && (
                <div style={{ ...styles.statTiny, color: "#eab308", marginTop: 6 }}>
                  ⚠ {schedulerHealth.advisories.cannibalization_count_30d} cannibalization advisor{schedulerHealth.advisories.cannibalization_count_30d === 1 ? "y" : "ies"} (advisory only — not a block; see Refresh Queue tab)
                </div>
              )}

              {/* QUEUE STATE */}
              <div style={{ ...styles.statTiny, color: "#888", borderTop: "1px solid #1a1a1a", paddingTop: 6, marginTop: 8 }}>
                Queue ({schedulerHealth.queue.month}): <strong style={{ color: "#fff" }}>{schedulerHealth.queue.approved_count}</strong> approved
                {schedulerHealth.queue.pending_count > 0 && (
                  <> · <span style={{ color: "#eab308" }}>{schedulerHealth.queue.pending_count} pending</span></>
                )}
                {schedulerHealth.queue.skipped_count > 0 && (
                  <> · {schedulerHealth.queue.skipped_count} skipped</>
                )}
                {schedulerHealth.queue.auto_approve_pct != null && schedulerHealth.queue.auto_approve_pct < 100 && (
                  <> · auto-approve <strong style={{ color: "#eab308" }}>{schedulerHealth.queue.auto_approve_pct}%</strong> (should be ~100%)</>
                )}
              </div>
            </div>
          </SubCard>
        )}

        {/* ── Phase 7.5 — Cost & Efficiency ──
            Pipeline runtime + output tokens + repair waste per client.
            $ unavailable — input_tokens not instrumented (followup ticket). */}
        {costEfficiency && (
          <SubCard
            severity={
              // amber if waste_pct > 30, red if > 50, else green
              costEfficiency.repair_waste?.waste_pct == null ? "green"
              : costEfficiency.repair_waste.waste_pct > 50 ? "red"
              : costEfficiency.repair_waste.waste_pct > 30 ? "amber"
              : "green"
            }
            label="Cost & Efficiency (last 30 days)"
          >
            <div>
              {/* RUNTIME — per-post avg */}
              <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ ...styles.statBig, fontSize: 22 }}>
                    {costEfficiency.runtime.per_post.avg_duration_ms != null
                      ? formatMs(costEfficiency.runtime.per_post.avg_duration_ms)
                      : "—"}
                  </div>
                  <div style={{ ...styles.statTiny, marginTop: -2 }}>avg / post ({costEfficiency.runtime.per_post.post_count} posts)</div>
                </div>
                <div>
                  <div style={{ ...styles.statBig, fontSize: 22, color: "#aaa" }}>
                    {costEfficiency.tokens.output_total > 0
                      ? formatNumber(costEfficiency.tokens.output_total)
                      : "—"}
                  </div>
                  <div style={{ ...styles.statTiny, marginTop: -2 }}>output tokens (30d)</div>
                </div>
                <div>
                  <div style={{ ...styles.statBig, fontSize: 22, color: "#aaa" }}>
                    {costEfficiency.tokens.output_per_published_post != null
                      ? formatNumber(costEfficiency.tokens.output_per_published_post)
                      : "—"}
                  </div>
                  <div style={{ ...styles.statTiny, marginTop: -2 }}>per published</div>
                </div>
              </div>

              {/* RUNTIME percentiles */}
              {costEfficiency.runtime.stage_count > 0 && (
                <div style={{ ...styles.statTiny, marginBottom: 8 }}>
                  Per-stage: avg <strong style={{ color: "#fff" }}>{formatMs(costEfficiency.runtime.avg_per_stage_ms)}</strong>
                  {" · "}p90 <strong style={{ color: "#fff" }}>{formatMs(costEfficiency.runtime.p90_per_stage_ms)}</strong>
                  {" · "}max <strong style={{ color: "#fff" }}>{formatMs(costEfficiency.runtime.max_per_stage_ms)}</strong>
                  {" "}({costEfficiency.runtime.stage_count} stages)
                </div>
              )}

              {/* MODEL BREAKDOWN */}
              {costEfficiency.model_breakdown.length > 0 && (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #1a1a1a" }}>
                  <div style={{ ...styles.statTiny, color: "#aaa", marginBottom: 4 }}>Model usage:</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {costEfficiency.model_breakdown.slice(0, 4).map((m) => (
                      <span key={m.model} style={{
                        fontSize: 9, padding: "2px 6px", borderRadius: 3,
                        background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.3)",
                        color: "#93c5fd", fontFamily: "'Barlow Condensed', sans-serif",
                        letterSpacing: "0.08em",
                      }} title={`${m.stages_count} stages, ${formatNumber(m.output_tokens)} tokens, ${formatMs(m.duration_ms)}`}>
                        {m.model.replace(/^claude-/, "")}: <strong>{formatNumber(m.output_tokens)}t</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* STAGE BREAKDOWN — top 3 by duration */}
              {costEfficiency.stage_breakdown.length > 0 && (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #1a1a1a" }}>
                  <div style={{ ...styles.statTiny, color: "#aaa", marginBottom: 4 }}>Top stages by time:</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {costEfficiency.stage_breakdown.slice(0, 3).map((s) => (
                      <div key={s.stage} style={{ ...styles.statTiny, color: "#aaa" }}>
                        <strong style={{ color: "#fff" }}>{s.stage}</strong>
                        {" "}— {s.calls} calls · {formatMs(s.total_duration_ms)} total · {formatNumber(s.output_tokens)}t
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* REPAIR WASTE */}
              {(costEfficiency.repair_waste.repair_exhausted_count > 0 || costEfficiency.repair_waste.repair_reverted_count > 0) ? (
                <div style={{
                  marginTop: 6, paddingTop: 6, borderTop: "1px solid #1a1a1a",
                  ...styles.statTiny,
                  color: costEfficiency.repair_waste.waste_pct > 30 ? "#fca5a5" : "#eab308",
                }}>
                  Repair waste: <strong>{costEfficiency.repair_waste.repair_reverted_count}</strong> reverted
                  {" · "}<strong>{costEfficiency.repair_waste.repair_exhausted_count}</strong> exhausted
                  {costEfficiency.repair_waste.waste_pct != null && (
                    <> ({costEfficiency.repair_waste.waste_pct}% of stage runs paid-for-and-discarded)</>
                  )}
                </div>
              ) : (
                <div style={{ ...styles.statTiny, color: "#86efac", marginTop: 4 }}>No repair waste.</div>
              )}

              {/* COST ESTIMATE — explicit unavailable state per Codex */}
              <div style={{
                marginTop: 8, paddingTop: 6, borderTop: "1px solid #1a1a1a",
                ...styles.statTiny, color: "#555",
              }}>
                ⓘ Dollar estimate unavailable
                {costEfficiency.cost_estimate.followup && (
                  <> — {costEfficiency.cost_estimate.followup}</>
                )}
              </div>
            </div>
          </SubCard>
        )}

        {/* ── Phase 7.2 — Per-Post Performance (compact + collapsible) ──
            Read-side shell — pulls from existing seo_keywords table.
            External-data refresh (GSC OAuth, SEMrush position cron) is
            spun out as separate tracked tasks. Card defaults collapsed
            per Codex (panel is getting crowded — 9th sub-card). */}
        {postPerformance && (
          <SubCard
            severity={
              !postPerformance.has_data ? "amber"
              : postPerformance.data_age?.is_stale ? "amber"
              : (postPerformance.trend?.avg_position_change != null && postPerformance.trend.avg_position_change < 0) ? "green"
              : "green"
            }
            label="Per-Post Performance (rankings)"
          >
            <div>
              {/* Compact header line — always visible */}
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div style={{ ...styles.statTiny, flex: 1 }}>
                  {postPerformance.has_data ? (
                    <>
                      <strong style={{ color: "#fff" }}>{postPerformance.coverage.matched_post_count}</strong>
                      {" / "}
                      <strong style={{ color: "#fff" }}>{postPerformance.coverage.total_published_posts}</strong>
                      {" posts ranked"}
                      {postPerformance.data_age?.is_stale && (
                        <span style={{ color: "#fca5a5", marginLeft: 8, fontWeight: 700 }}>
                          ⚠ data {postPerformance.data_age.days_since_newest}d stale
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: "#888" }}>No ranking data tracked yet</span>
                  )}
                </div>
                <button
                  onClick={() => setPerfExpanded(v => !v)}
                  style={{
                    fontSize: 9, padding: "2px 8px", borderRadius: 3,
                    background: "transparent", border: "1px solid #2a2a2a",
                    color: "#888", cursor: "pointer",
                    fontFamily: "'Barlow Condensed', sans-serif",
                    letterSpacing: "0.1em", textTransform: "uppercase",
                  }}
                >
                  {perfExpanded ? "Hide" : "Show details"}
                </button>
              </div>

              {/* Empty state when no data */}
              {!postPerformance.has_data && postPerformance.empty_state_message && (
                <div style={{ ...styles.statTiny, color: "#666", marginTop: 6 }}>
                  {postPerformance.empty_state_message}
                </div>
              )}

              {/* Expanded details */}
              {perfExpanded && postPerformance.has_data && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #1a1a1a" }}>
                  {/* Trend pill */}
                  {postPerformance.trend?.sample_size > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{
                        ...styles.ratioPill,
                        background: postPerformance.trend.avg_position_change < 0
                          ? "rgba(34,197,94,0.15)"
                          : postPerformance.trend.avg_position_change > 0
                            ? "rgba(214,0,0,0.15)"
                            : "rgba(255,255,255,0.06)",
                        color: postPerformance.trend.avg_position_change < 0
                          ? "#86efac"
                          : postPerformance.trend.avg_position_change > 0
                            ? "#fca5a5"
                            : "#aaa",
                      }}>
                        {postPerformance.trend.avg_position_change > 0 ? "+" : ""}
                        {postPerformance.trend.avg_position_change.toFixed(1)} avg position Δ
                      </span>
                      <span style={{ ...styles.statTiny, marginLeft: 8 }}>
                        ({postPerformance.trend.sample_size} posts in trend, negative = improving)
                      </span>
                    </div>
                  )}

                  {/* Top 5 by traffic */}
                  {postPerformance.top_by_traffic.length > 0 && (
                    <div>
                      <div style={{ ...styles.statTiny, color: "#aaa", marginBottom: 4 }}>
                        Top by estimated traffic:
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {postPerformance.top_by_traffic.map(p => (
                          <div key={p.post_id} style={{ ...styles.statTiny, color: "#aaa", display: "flex", gap: 6, alignItems: "baseline" }}>
                            <span style={{ color: "#fff", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.keyword}</span>
                            <span style={{ color: "#888" }}>#{p.position}</span>
                            <span style={{ color: "#888" }}>{p.traffic} hits</span>
                            {typeof p.position_change === "number" && p.position_change !== 0 && (
                              <span style={{ color: p.position_change < 0 ? "#86efac" : "#fca5a5" }}>
                                {p.position_change > 0 ? "+" : ""}{p.position_change}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Stale notice (when expanded) */}
                  {postPerformance.data_age?.is_stale && (
                    <div style={{ ...styles.statTiny, color: "#555", marginTop: 8, paddingTop: 6, borderTop: "1px solid #1a1a1a" }}>
                      Newest snapshot: {new Date(postPerformance.data_age.newest_snapshot_at).toLocaleDateString()}.
                      Refresh cron (followup task) will keep this current.
                    </div>
                  )}
                </div>
              )}
            </div>
          </SubCard>
        )}
      </div>

      {!anyAlerts && (
        <button onClick={() => setExpanded(false)} style={styles.collapseToggleBottom}>Collapse</button>
      )}
    </div>
  );
}

function SubCard({ severity, label, children }) {
  const accent = severity === "red" ? "#d60000" : severity === "amber" ? "#eab308" : "#22c55e";
  return (
    <div style={{ ...styles.subCard, borderTop: `2px solid ${accent}` }}>
      <div style={{ ...styles.subLabel, color: accent }}>{label}</div>
      {children}
    </div>
  );
}

// Phase 7.5 — pretty-print milliseconds as "Nms", "N.Ns", or "Nm Ms"
function formatMs(n) {
  if (n == null || !isFinite(n)) return "—";
  const ms = Math.round(n);
  if (ms < 1000) return `${ms}ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const min = Math.floor(sec / 60);
  const remSec = Math.round(sec - min * 60);
  return remSec > 0 ? `${min}m ${remSec}s` : `${min}m`;
}

// Phase 7.5 — pretty-print a number with thousands separator
function formatNumber(n) {
  if (n == null || !isFinite(n)) return "—";
  return Math.round(n).toLocaleString();
}

// Phase 7.3 — pretty-print a minutes value as "Nm", "Nh Mm", or "Nd Mh"
function formatMinutes(n) {
  if (n == null || !isFinite(n)) return "—";
  const min = Math.round(n);
  if (min < 60) return `${min}m`;
  const hours = Math.floor(min / 60);
  const rem = min % 60;
  if (hours < 24) return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const hRem = hours % 24;
  return hRem > 0 ? `${days}d ${hRem}h` : `${days}d`;
}

const styles = {
  shell: {
    background: "#0d0d0d",
    border: "1px solid #1a1a1a",
    borderRadius: 4,
    padding: "14px 18px",
    marginTop: 18,
    marginBottom: 18,
  },
  loading: { color: "#666", fontSize: 12, padding: "6px 0", fontStyle: "italic" },
  errorBanner: {
    color: "#fca5a5",
    fontSize: 12,
    background: "rgba(214,0,0,0.08)",
    border: "1px solid rgba(214,0,0,0.3)",
    padding: "8px 12px",
    borderRadius: 3,
  },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 },
  eyebrow: {
    fontSize: 10,
    color: "#d60000",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontFamily: "'Barlow Condensed', sans-serif",
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#fff",
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: "0.02em",
    marginTop: 2,
  },
  refreshBtn: {
    background: "transparent",
    border: "1px solid #2a2a2a",
    color: "#888",
    fontSize: 14,
    padding: "4px 10px",
    borderRadius: 3,
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
  },
  subCard: {
    background: "#0a0a0a",
    border: "1px solid #1a1a1a",
    borderRadius: 3,
    padding: "12px 14px",
    minHeight: 100,
  },
  subLabel: {
    fontSize: 10,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 700,
    marginBottom: 10,
  },
  statBig: { fontSize: 14, color: "#fff", fontWeight: 600, fontFamily: "'Barlow Condensed', sans-serif" },
  statSub: { fontSize: 12, color: "#888", lineHeight: 1.4, marginTop: 4 },
  statTiny: { fontSize: 10, color: "#555", fontStyle: "italic", lineHeight: 1.4, marginTop: 6 },
  ratioRow: { display: "flex", alignItems: "center", gap: 6, marginBottom: 4 },
  ratioPill: {
    padding: "2px 8px",
    borderRadius: 3,
    fontSize: 11,
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.06em",
    fontWeight: 700,
  },
  ratioDivider: { color: "#444", fontSize: 12 },
  barWrap: { background: "#111", height: 4, borderRadius: 2, overflow: "hidden", marginTop: 8 },
  barFill: { height: "100%", borderRadius: 2, transition: "width 0.4s ease" },
  invList: { display: "grid", gap: 4, marginTop: 6 },
  invRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 11,
    fontFamily: "'Barlow Condensed', sans-serif",
    padding: "2px 6px",
    background: "rgba(255,255,255,0.02)",
    borderRadius: 2,
  },
  invLoc: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  invCount: {
    background: "rgba(255,255,255,0.06)",
    padding: "1px 6px",
    borderRadius: 999,
    fontSize: 10,
    fontVariantNumeric: "tabular-nums",
    marginLeft: 8,
  },
  invMissingSap: { fontSize: 9, color: "#fca5a5", marginLeft: 6, fontStyle: "italic" },
  collapsedRow: { display: "flex", alignItems: "center", gap: 12 },
  collapsedIcon: { fontSize: 18, color: "#22c55e" },
  collapsedText: { flex: 1, fontSize: 12, color: "#aaa", fontFamily: "'Barlow', sans-serif" },
  collapsedToggle: {
    background: "transparent",
    border: "1px solid #2a2a2a",
    color: "#aaa",
    fontSize: 11,
    padding: "5px 12px",
    borderRadius: 3,
    cursor: "pointer",
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  collapseToggleBottom: {
    background: "transparent",
    border: "1px solid #2a2a2a",
    color: "#888",
    fontSize: 10,
    padding: "4px 10px",
    borderRadius: 3,
    cursor: "pointer",
    marginTop: 12,
    fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
};
